import { useState, useEffect } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, WhatsappLogo, PencilSimple, Trash, Buildings } from "@phosphor-icons/react";
import { buildVendorVars } from "../utils/whatsappContext";

const Vendors = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editor, setEditor] = useState(null);
  const [waModal, setWaModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vendors${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setItems(r.data || []);
    } catch { toast.error("Failed to load vendors"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!editor.name) { toast.error("Vendor name required"); return; }
    try {
      if (editor.vendor_id) {
        await axios.put(`${API}/vendors/${editor.vendor_id}?requester_role=${user?.role || ""}`, editor);
      } else {
        await axios.post(`${API}/vendors?requester_role=${user?.role || ""}`, editor);
      }
      toast.success("Saved");
      setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor ${v.name}?`)) return;
    try { await axios.delete(`${API}/vendors/${v.vendor_id}?requester_role=${user?.role || ""}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openWa = (v, action) => {
    setWaModal({
      module: "vendor",
      receiver: { name: v.contact_person || v.name, phone: v.whatsapp_number || v.phone, type: "vendor", id: v.vendor_id },
      defaultAction: action,
      context: { variables: buildVendorVars(v) },
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Procurement</div>
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Vendors</h1><HelpButton moduleKey="vendor" /></div>
            <p className="text-base text-slate-700 mt-1">{items.length} suppliers · click WhatsApp to chat or send PO</p>
          </div>
          <button data-testid="vendor-new-btn" onClick={() => setEditor({ name: "", contact_person: "", phone: "", whatsapp_number: "", email: "", category: "", status: "active" })}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"><Plus size={16} weight="bold" /> Add Vendor</button>
        </div>

        <div className="bg-white border border-slate-200 p-4 mb-4">
          <div className="relative max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="vendor-search" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search vendors…" className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 pl-9 outline-none text-sm" />
          </div>
        </div>

        <div data-testid="vendors-table" className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["Vendor", "Contact", "WhatsApp", "Category", "Status", "Actions"].map(h => <th key={h} className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-12 text-center text-slate-400">Loading…</td></tr> :
              items.length === 0 ? <tr><td colSpan={6} className="p-12 text-center text-slate-400 text-sm">No vendors yet. Click <strong>Add Vendor</strong>.</td></tr> :
              items.map((v, i) => (
                <tr key={v.vendor_id} data-testid={`vendor-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{v.name}</div>
                    <div className="text-xs text-slate-500">{v.vendor_code}</div>
                  </td>
                  <td className="p-3 text-sm">
                    <div>{v.contact_person || "—"}</div>
                    <div className="text-xs text-slate-500">{v.email || ""}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{v.whatsapp_number ? `+${v.whatsapp_number}` : <span className="text-slate-400">—</span>}</td>
                  <td className="p-3 text-xs uppercase tracking-wider"><span className="bg-slate-100 text-slate-700 px-2 py-0.5 border border-slate-200">{v.category || "—"}</span></td>
                  <td className="p-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${v.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{v.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {v.whatsapp_number && (<>
                      <button data-testid={`vendor-wa-chat-${i}`} onClick={() => openWa(v, "chat")} className="p-1.5 text-emerald-700 hover:bg-emerald-50 ml-1" title="Chat with vendor"><WhatsappLogo size={14} weight="fill" /></button>
                      <button data-testid={`vendor-wa-po-${i}`} onClick={() => openWa(v, "send_po")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 ml-1 font-bold">Send PO</button>
                    </>)}
                    <button onClick={() => setEditor(v)} className="p-1.5 text-slate-700 hover:bg-slate-100 ml-1"><PencilSimple size={14} /></button>
                    {(user?.role === "admin" || user?.role === "manager") && (
                      <button onClick={() => remove(v)} className="p-1.5 text-rose-700 hover:bg-rose-50 ml-1"><Trash size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" data-testid="vendor-editor">
          <div className="bg-white border border-slate-200 w-full max-w-xl shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2"><Buildings size={18} className="text-[#002FA7]" /><h3 className="font-semibold">{editor.vendor_id ? "Edit" : "New"} Vendor</h3></div>
            <div className="p-5 space-y-3">
              {[
                ["name", "Vendor / Company name *"], ["contact_person", "Contact person"],
                ["phone", "Phone"], ["whatsapp_number", "WhatsApp (91XXXXXXXXXX)"],
                ["email", "Email"], ["gstin", "GSTIN"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">{l}</label>
                  <input data-testid={`vendor-fld-${k}`} value={editor[k] || ""} onChange={e => setEditor({...editor, [k]: e.target.value})} className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Category</label>
                  <select value={editor.category || ""} onChange={e => setEditor({...editor, category: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {["", "cctv", "nvr", "biometric", "accessories", "services", "other"].map(c => <option key={c} value={c}>{c || "— select —"}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Status</label>
                  <select value={editor.status || "active"} onChange={e => setEditor({...editor, status: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {["active", "inactive", "blacklisted"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setEditor(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="vendor-save" onClick={save} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}

      {waModal && <WhatsAppSendModal open={true} onClose={() => setWaModal(null)} {...waModal} user={user} />}
    </div>
  );
};

export default Vendors;
